import { motion } from "framer-motion";
import { Wrench, Clock, ArrowRight, ShieldCheck, Mail } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import cpLogoLight from "@/assets/cp-logo-light.png";
import cpLogoDark from "@/assets/cp-logo-dark.png";
import { useIsMobile } from "@/hooks/use-mobile";

interface MaintenancePageProps {
  message?: string;
}

const MaintenancePage = ({ message }: MaintenancePageProps) => {
  const { theme } = useTheme();
  const isMobile = useIsMobile();

  return (
    <main className="relative min-h-screen bg-background overflow-hidden flex items-center justify-center p-4">
      {/* Background Orbs matching HeroSection */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
        <div className="absolute top-1/4 left-1/4 w-[300px] h-[300px] sm:w-[500px] sm:h-[500px] bg-primary/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-1/4 right-1/4 w-[250px] h-[250px] sm:w-[400px] sm:h-[400px] bg-accent/10 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
      </div>

      <div className="relative z-10 max-w-xl w-full">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="text-center"
        >
          {/* Logo */}
            <div className="inline-block p-1">
              <img
                src={theme === "dark" ? cpLogoDark : cpLogoLight}
                alt="Chawni"
                width={isMobile ? 96 : 144}
                height={isMobile ? 96 : 144}
                className={`w-24 h-24 sm:w-36 sm:h-36 object-contain ${theme === "dark" ? "mix-blend-screen" : "mix-blend-multiply"}`}
                fetchPriority="high"
                decoding="async"
              />
            </div>

          {/* Heading */}
          <h1 className="text-3xl sm:text-5xl font-display font-extrabold mb-4 text-foreground tracking-tight">
            We'll be back{" "}
            <span className="bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
              shortly
            </span>
          </h1>

          <p className="text-base sm:text-lg text-foreground/80 mb-8 max-w-md mx-auto leading-relaxed">
            {message || "We're currently upgrading our systems to bring you an even better Chawni experience."}
          </p>

          {/* Premium Info Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="bg-card/60 backdrop-blur-xl rounded-3xl border border-primary/10 p-6 sm:p-8 shadow-xl shadow-primary/5 mb-8 text-left relative overflow-hidden"
          >
            {/* Gloss reflection overlay */}
            <div className="absolute inset-x-0 -top-px h-px bg-gradient-to-r from-transparent via-white/50 to-transparent" />
            
            <div className="flex flex-col sm:flex-row gap-6 items-center sm:items-start text-center sm:text-left">
              <div className="relative shrink-0">
                <div className="absolute inset-0 rounded-full bg-primary/20 animate-ping opacity-30" />
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 flex items-center justify-center relative shadow-inner">
                  <Wrench className="w-6 h-6 text-primary animate-[spin_4s_ease-in-out_infinite]" />
                </div>
              </div>
              
              <div className="space-y-3">
                <h2 className="font-semibold text-foreground text-lg">Scheduled Maintenance</h2>
                <div className="flex items-center justify-center sm:justify-start gap-2 text-sm text-foreground/70">
                  <Clock className="w-4 h-4 text-accent" />
                  <span>Our engineers are currently working on improvements.</span>
                </div>
                <div className="flex items-center justify-center sm:justify-start gap-2 text-sm text-foreground/70">
                  <ShieldCheck className="w-4 h-4 text-emerald-600" />
                  <span>Your data and existing orders remain perfectly safe.</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Contact / Footer Actions */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.5 }}
            className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-sm"
          >
            <a
              href="https://wa.me/212647424567"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition-colors font-medium border border-primary/10"
            >
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              WhatsApp Support
            </a>
            <a
              href="mailto:support@chawniphone.com"
              className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-muted text-foreground hover:bg-muted/80 transition-colors font-medium border border-border"
            >
              <Mail className="w-4 h-4" />
              Email Us
            </a>
          </motion.div>
        </motion.div>
      </div>
    </main>
  );
};

export default MaintenancePage;
